import { memo, useEffect, useState } from 'react';
import { api } from '../api.ts';
import { parseUtc } from '../format.ts';
import type { Comment, Version, Viewer } from '../types.ts';
import { formatShortTimecode } from '../timecode.ts';
import { Chevron } from './Chevron.tsx';

interface Props {
  version: Version | null;
  comments: Comment[];
  currentFrame: number;
  fps: number;
  /** Source clip's first frame number, added to stored 0-based frames only for display. */
  startFrame: number;
  onJumpToFrame: (frame: number) => void;
  onAddComment: (body: string, anchorToFrame: boolean) => Promise<void>;
  onDeleteComment: (id: string) => void;
  busy?: boolean;
  /** Panel expanded; when false it folds to a thin rail. */
  open?: boolean;
  onToggle?: () => void;
  /** Host-only: shows the second "Viewed by" tab. Everyone else sees the notes list, unchanged. */
  canSeeViewers?: boolean;
  sessionId?: string;
}

/** Köln time, whichever timezone the viewing browser is set to — the studio's own clock. */
function studioTime(sqliteUtc: string): string {
  return parseUtc(sqliteUtc).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/** Who has viewed the current clip within this review. Fetched fresh on every clip switch. */
function ViewersList({ versionId, sessionId }: { versionId: string; sessionId: string }): React.JSX.Element {
  const [viewers, setViewers] = useState<Viewer[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setViewers(null);
    void api
      .getVersionViews(versionId, sessionId)
      .then((res) => {
        if (!cancelled) setViewers(res.views);
      })
      .catch(() => {
        if (!cancelled) setViewers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [versionId, sessionId]);

  if (viewers === null) return <p className="comments__empty">Loading…</p>;
  if (viewers.length === 0) return <p className="comments__empty">Nobody has viewed this clip yet.</p>;

  return (
    <ul className="viewers__list">
      {viewers.map((v) => (
        <li key={v.guestName} className="viewers__row">
          <strong className={v.decision ? `decision--${v.decision}` : undefined}>{v.guestName}</strong>
          {/* When this person first opened the clip — later looks do not move it. */}
          <span className="muted">{studioTime(v.firstViewedAt)}</span>
        </li>
      ))}
    </ul>
  );
}

interface ListProps {
  comments: Comment[];
  fps: number;
  startFrame: number;
  onJumpToFrame: (frame: number) => void;
  onDeleteComment: (id: string) => void;
}

/**
 * The notes themselves, split out and memoised.
 *
 * The panel around it re-renders on every displayed video frame, because the compose box shows
 * the live playhead — but nothing in this list depends on it, and re-rendering a review's worth
 * of notes and drawing thumbnails twenty-five times a second for a label is the expensive half.
 */
const CommentList = memo(function CommentList({
  comments,
  fps,
  startFrame,
  onJumpToFrame,
  onDeleteComment,
}: ListProps): React.JSX.Element {
  return (
    <div className="comments__list">
      {comments.length === 0 ? <p className="comments__empty">No notes yet.</p> : null}
      {comments.map((comment) => (
        <article key={comment.id} className="comment">
          <header className="comment__head">
            <strong>{comment.author}</strong>
            {comment.frame !== null ? (
              <button
                type="button"
                className="comment__frame"
                onClick={() => onJumpToFrame(comment.frame ?? 0)}
                title="Jump to this frame"
              >
                {formatShortTimecode(comment.frame, fps)} · f{startFrame + comment.frame}
              </button>
            ) : (
              <span className="comment__frame comment__frame--general">general</span>
            )}
            <button
              type="button"
              className="comment__delete"
              onClick={() => onDeleteComment(comment.id)}
              title="Delete note"
            >
              ×
            </button>
          </header>
          <p className="comment__body">{comment.body}</p>
          {comment.annotations.length > 0 ? (
            <div className="comment__annotations">
              {comment.annotations.map((a) =>
                a.png ? (
                  <button
                    key={a.id}
                    type="button"
                    className="comment__thumb"
                    onClick={() => onJumpToFrame(a.frame)}
                    title={`Drawing on frame ${startFrame + a.frame}`}
                  >
                    <img src={a.png} alt={`Drawing frame ${startFrame + a.frame}`} />
                  </button>
                ) : (
                  <span key={a.id} className="comment__drawn">
                    Drawing f{startFrame + a.frame}
                  </span>
                ),
              )}
            </div>
          ) : null}
        </article>
      ))}
    </div>
  );
});

export function CommentPanel({
  version,
  comments,
  currentFrame,
  fps,
  startFrame,
  onJumpToFrame,
  onAddComment,
  onDeleteComment,
  busy,
  open = true,
  onToggle,
  canSeeViewers = false,
  sessionId,
}: Props): React.JSX.Element {
  const [draft, setDraft] = useState('');
  const [anchor, setAnchor] = useState(true);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState<'notes' | 'viewers'>('notes');

  const submit = async (): Promise<void> => {
    const text = draft.trim();
    if (text === '' || sending) return;
    setSending(true);
    try {
      await onAddComment(text, anchor);
      setDraft('');
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <aside className="comments comments--collapsed">
        <button
          type="button"
          className="comments__toggle comments__toggle--rail"
          onClick={onToggle}
          title="Show notes"
          aria-label="Show notes"
        >
          <Chevron dir="left" />
          <span className="comments__rail-label">Notes</span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="comments">
      <h2 className="comments__title">
        {onToggle ? (
          <button
            type="button"
            className="comments__toggle"
            onClick={onToggle}
            title="Collapse notes"
            aria-label="Collapse notes"
          >
            <Chevron dir="right" />
          </button>
        ) : null}
        {canSeeViewers ? (
          <span className="comments__tabs">
            <button
              type="button"
              className={`comments__tab${tab === 'notes' ? ' comments__tab--active' : ''}`}
              onClick={() => setTab('notes')}
            >
              Notes
            </button>
            <button
              type="button"
              className={`comments__tab${tab === 'viewers' ? ' comments__tab--active' : ''}`}
              onClick={() => setTab('viewers')}
            >
              Viewed by
            </button>
          </span>
        ) : (
          'Notes'
        )}
        {version ? <span className="comments__subtitle">{version.label}</span> : null}
      </h2>

      {tab === 'viewers' && canSeeViewers ? (
        <div className="comments__list">
          {version && sessionId ? (
            <ViewersList key={version.id} versionId={version.id} sessionId={sessionId} />
          ) : (
            <p className="comments__empty">No clip selected.</p>
          )}
        </div>
      ) : (
        <CommentList
          comments={comments}
          fps={fps}
          startFrame={startFrame}
          onJumpToFrame={onJumpToFrame}
          onDeleteComment={onDeleteComment}
        />
      )}

      {tab === 'notes' ? (
        <div className="comments__compose">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a note…"
            rows={3}
            disabled={busy || !version}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter makes a new line.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
              e.stopPropagation();
            }}
          />
          <label className="comments__anchor">
            <input type="checkbox" checked={anchor} onChange={(e) => setAnchor(e.target.checked)} />
            pin to frame {currentFrame}
          </label>
          <button type="button" onClick={() => void submit()} disabled={sending || draft.trim() === ''}>
            {sending ? 'sending…' : 'Send note'}
          </button>
        </div>
      ) : null}
    </aside>
  );
}
